

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Grid, Icon, Snackbar, Alert } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewTravelInvoiceLineItemModal from 'components/NewTravelInvoiceLineItemModal';
import { formatDate } from 'utils/DateTimeConversion';

const TravelInvoiceLineItems = () => {
    const [lineItems, setLineItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('travel_invoice_line_items');

    useEffect(() => {
        fetchLineItems();
    }, []);

    const fetchLineItems = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/travel-invoice-line-items/');
            setLineItems(response.data);
        } catch (error) {
            console.error('Error fetching travel invoice line items:', error);
            setSnackbar({ open: true, message: 'Error loading travel invoice line items', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveLineItem = async (payload) => {
        try {
            await axiosInstance.post('/travel-invoice-line-items/', payload);
            setSnackbar({ open: true, message: 'Travel Invoice Line Item created successfully', severity: 'success' });
            fetchLineItems();
        } catch (error) {
            console.error('Error saving travel invoice line item:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/travel-invoice-line-item/${rowData.id}`);
    };

    const formatCurrency = (amount, currency = 'GBP') => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'GBP',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(parseFloat(amount));
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                >
                    {value || `Line Item #${row.original.id}`}
                </MDTypography>
            )
        },
        {
            Header: "Invoice",
            accessor: "invoice_id",
            width: "12%",
            Cell: ({ row }) => row.original.invoice?.name || row.original.invoice_id || '-'
        },
        {
            Header: "Order",
            accessor: "order_id",
            width: "12%",
            Cell: ({ row }) => row.original.order?.name || row.original.order_id || '-'
        },
        {
            Header: "Amount",
            accessor: "amount",
            width: "12%",
            Cell: ({ value, row }) => formatCurrency(value, row.original.currency)
        },
        {
            Header: "Currency",
            accessor: "currency",
            width: "8%"
        },
        {
            Header: "Status",
            accessor: "status",
            width: "12%"
        },
        {
            Header: "Invoice Date",
            accessor: "invoice_date",
            width: "12%",
            Cell: ({ value }) => value ? formatDate(value) : '-'
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "5%",
            Cell: ({ value }) => value ? formatDate(value) : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: lineItems,
    }), [lineItems]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Travel Invoice Line Items</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Line Item
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : lineItems.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                        onRowClick={handleRowClick}
                                    />
                                ) : (
                                    <EmptyState moduleName="Travel Invoice Line Item" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewTravelInvoiceLineItemModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveLineItem}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TravelInvoiceLineItems;

